// Runtime configuration, served as /config.json by the web container so the
// same build runs in every environment (DEP-010: promote, don't rebuild).

export type Environment = 'development' | 'staging' | 'production';

export interface OIDCConfig {
  /** Keycloak realm URL, e.g. https://auth.illumilink.io/realms/illumilink */
  authority: string;
  clientId: string;
}

export interface RuntimeConfig {
  apiUrl: string;
  environment: Environment;
  /** Sign-in is enabled when present. */
  oidc?: OIDCConfig;
}

const ENVIRONMENTS: readonly Environment[] = ['development', 'staging', 'production'];

function requireURL(name: string, value: unknown, environment: Environment): string {
  if (typeof value !== 'string' || !URL.canParse(value)) {
    throw new Error(`config.json: ${name} must be an absolute URL`);
  }
  if (environment !== 'development' && new URL(value).protocol !== 'https:') {
    throw new Error(`config.json: ${name} must use https outside development`);
  }
  return value.replace(/\/+$/, '');
}

export function parseConfig(raw: unknown): RuntimeConfig {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('config.json must be a JSON object');
  }
  const { apiUrl, environment, oidc } = raw as Record<string, unknown>;

  if (typeof environment !== 'string' || !ENVIRONMENTS.includes(environment as Environment)) {
    throw new Error(`config.json: environment must be one of ${ENVIRONMENTS.join(', ')}`);
  }
  const env = environment as Environment;
  const config: RuntimeConfig = { apiUrl: requireURL('apiUrl', apiUrl, env), environment: env };

  if (oidc !== undefined) {
    if (typeof oidc !== 'object' || oidc === null) {
      throw new Error('config.json: oidc must be an object');
    }
    const { authority, clientId } = oidc as Record<string, unknown>;
    if (typeof clientId !== 'string' || clientId === '') {
      throw new Error('config.json: oidc.clientId is required');
    }
    config.oidc = { authority: requireURL('oidc.authority', authority, env), clientId };
  }
  return config;
}

export async function loadConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  const res = await fetchImpl('/config.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to load /config.json (HTTP ${String(res.status)})`);
  }
  return parseConfig(await res.json());
}
